import { AmChartsModule } from '@amcharts/amcharts3-angular';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import de from '@angular/common/locales/de';
import extraDe from '@angular/common/locales/extra/de';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideEnvironmentInitializer
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { KwPipe, LocalStorageService, provideCinetixxUi, STORAGE_STRATEGY } from '@cinetixx/cinetixx-ui';
import * as Sentry from '@sentry/angular';
import { DragulaModule } from 'ng2-dragula';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { provideNgxMask } from 'ngx-mask';
import { catchError, of, switchMap } from 'rxjs';
import { routes } from './app.routes';
import { MIXED_NAME, ORDER_OF_ROWS, ORDER_OF_SEATS, SHAPE_SEAT_TYPES } from './auditoriums/core/constants';
import { ShapeSeatTypes } from './auditoriums/core/enums';
import { ShapeManager } from './auditoriums/core/managers';
import { AuthService } from './auth/core/services/AuthService';
import { HttpRequestInterceptor } from './core/interceptors/http-request.interceptor';
import { HttpTokenInterceptor } from './core/interceptors/http-token.interceptor';
import { AppService } from './core/services/AppService';
import { IdentityService } from './core/services/IdentityService';
import { StaticDependenciesService } from './core/services/StaticDependenciesService';
import { TableConfigDatabaseStrategy } from './core/strategies/TableConfigDatabaseStrategy';
import { TableStateDatabaseStrategy } from './core/strategies/TableStateDatabaseStrategy';
import { tryRecoverFromChunkLoadError } from './core/utils/chunk-load-error.util';
import { environment } from '../environments/environment';
import { TableExportStrategy } from './exports/core/strategies/TableExportStrategy';
import { languagesSnippets$ } from './language/core/guards/languages-snippets.guard';
import { CalendarService } from './planning/core/services/CalendarService';
import { CopyWeekAdvancedCalendarService } from './planning/core/services/CopyWeekAdvancedCalendarService';
import { SwapAuditoriumCalendarService } from './planning/core/services/SwapAuditioriumCalendarService';
import { FormattedTimeToSecondsPipe } from './tube/core/pipes/formatted-time-to-seconds.pipe';
import { SecondsToFormattedTimePipe } from './tube/core/pipes/seconds-to-formatted-time.pipe';
import { VariableElementCheckPipe } from './tube/core/pipes/variable-element-check.pipe';
import { ScreenerCalendarService } from './tube/core/services/ScreenerCalendarService';
import { RewardsManagerService } from './vouchers/core/services/RewardsManagerService';

function setLocales(): void {
  const lang = location.pathname.split('/')[1];

  Promise.all([
    import(
      /* webpackExclude: /\.(d\.ts|js\.map)$/ */
      /* webpackMode: "lazy-once" */
      /* webpackChunkName: "i18n-extra" */

      `@/../node_modules/@angular/common/locales/${ lang }`
      ),
    import(
      /* webpackExclude: /\.(d\.ts|js\.map)$/ */
      /* webpackMode: "lazy-once" */
      /* webpackChunkName: "i18n-extra" */

      `@/../node_modules/@angular/common/locales/extra/${ lang }`
      )
  ]).then(([module1, module2]) => registerLocaleData(module1.default, 'main', module2.default))
    .catch(err => registerLocaleData(de, 'main', extraDe));
}

setLocales();

const monacoMinBaseUrl = `${window.location.origin}/assets/monaco/min/`;
const monacoVsBaseUrl = `${monacoMinBaseUrl}vs`;

function configureMonacoEnvironment(): void {
  (window as unknown as { MonacoEnvironment: { getWorkerUrl: () => string } }).MonacoEnvironment = {
    getWorkerUrl: (): string => {
      const bootstrap = `
        self.MonacoEnvironment = { baseUrl: '${monacoMinBaseUrl}' };
        importScripts('${monacoVsBaseUrl}/base/worker/workerMain.js');
      `;
      return `data:text/javascript;charset=utf-8,${encodeURIComponent(bootstrap)}`;
    },
  };
}

configureMonacoEnvironment();

function isMonacoCancellationError(error: unknown): boolean {
  const candidates = [
    error,
    (error as { rejection?: unknown } | null)?.rejection,
    (error as { originalError?: unknown } | null)?.originalError,
    (error as { ngOriginalError?: unknown } | null)?.ngOriginalError,
  ];

  return candidates.some((candidate) => {
    if (!candidate || typeof candidate !== 'object') {
      return typeof candidate === 'string' && candidate.includes('Canceled');
    }

    const name = 'name' in candidate ? String((candidate as { name: unknown }).name) : '';
    const message = 'message' in candidate ? String((candidate as { message: unknown }).message) : '';
    return name === 'Canceled' || message === 'Canceled' || message === 'Canceled: Canceled';
  });
}

const sentryErrorHandler = Sentry.createErrorHandler({
  showDialog: false,
});

function buildCinetixxUiConfig(lang: string) {
  return {
    transFunction: String().trans,
    app: environment.app,
    defaultTimeFormat: environment.defaultTimeFormat,
    defaultDateFormat: environment.defaultDateFormat,
    lang,
    production: environment.production,
    cloudFrontUrl: environment.cloudFrontUrl,
    defaultTableStateStrategy: TableStateDatabaseStrategy,
    defaultTableConfigStrategy: TableConfigDatabaseStrategy<any>,
    defaultTableExportStrategy: TableExportStrategy,
    fallbackLanguage: environment.fallbackLanguage,
    brand: environment.brand
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling({
      scrollPositionRestoration: 'enabled'
    })),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.isSW
    }),
    importProvidersFrom(
      AmChartsModule,
      DragulaModule.forRoot(),
      MonacoEditorModule.forRoot({
        baseUrl: monacoVsBaseUrl,
        onMonacoLoad: configureMonacoEnvironment,
      }),
    ),
    {
      provide: LOCALE_ID,
      useValue: 'main'
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR'
    },
    {
      provide: STORAGE_STRATEGY,
      useFactory: (localStorageService: LocalStorageService) => localStorageService,
      deps: [LocalStorageService],
    },
    {
      provide: ErrorHandler,
      useValue: {
        handleError(error: unknown): void {
          if (isMonacoCancellationError(error)) {
            return;
          }
          if (tryRecoverFromChunkLoadError(error)) {
            return;
          }
          sentryErrorHandler.handleError(error);
        },
      },
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
      const appService = inject(AppService);
      const authService = inject(AuthService);
      const identityService = inject(IdentityService);

      return appService.loadApp()
        .pipe(
          switchMap(() => {
            if (identityService.identity) {
              return authService.getStatus();
            }

            return of(true);
          }),
          catchError(() => of(true))
        );
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTokenInterceptor,
      multi: true
    },
    StaticDependenciesService,
    provideCinetixxUi(buildCinetixxUiConfig(environment.fallbackLanguage)),
    // Feature providers migrated from deleted *.module.ts files (formerly module-scoped, now app-wide)
    provideNgxMask(),
    KwPipe,
    ShapeManager,
    provideEnvironmentInitializer(() => {
      inject(StaticDependenciesService);
      inject(ShapeManager);

      languagesSnippets$.subscribe(() => {
        provideCinetixxUi(buildCinetixxUiConfig(StaticDependenciesService.languageStoreService.language.token));
      });
    }),
    {
      provide: ORDER_OF_ROWS,
      useFactory: () => [
        {
          id: 1,
          name: 'top to bottom'.trans('auditoriums/generate-set-of-seats-form/rows-order/top-to-bottom.Text'),
          default: true
        },
        {
          id: 2,
          name: 'bottom to top'.trans('auditoriums/generate-set-of-seats-form/rows-order/bottom-to-top.Text'),
        }
      ]
    },
    {
      provide: ORDER_OF_SEATS,
      useFactory: () => [
        {
          id: 1,
          name: 'left to right'.trans('auditoriums/generate-set-of-seats-form/seats-order/left-to-right.Text'),
          default: true
        },
        {
          id: 2,
          name: 'right to left'.trans('auditoriums/generate-set-of-seats-form/seats-order/right-to-left.Text'),
        }
      ]
    },
    {
      provide: SHAPE_SEAT_TYPES,
      useFactory: () => Object.values(ShapeSeatTypes)
        .filter(x =>
          x !== ShapeSeatTypes.NONE
          && x !== ShapeSeatTypes.CON_SEAT
          && x !== ShapeSeatTypes.LOVE_SEAT_RIGHT
        )
        .map((x) => ({
          id: x,
          name: x === ShapeSeatTypes.LOVE_SEAT_LEFT
            ? 'Love seat'.trans('auditoriums/add-edit-shape-form/form/control/shape-seat-type/love-seat.Label')
            : x === ShapeSeatTypes.NORMAL_CHAIR
              ? 'Normal Chair'.trans('auditoriums/add-edit-shape-form/form/control/shape-seat-type/normal-chair.Label')
              : x
        }))
    },
    {
      provide: MIXED_NAME,
      useFactory: () => '—Mixed—'.trans('auditoriums/creator/settings/mixed.Label')
    },
    SecondsToFormattedTimePipe,
    FormattedTimeToSecondsPipe,
    ScreenerCalendarService,
    VariableElementCheckPipe,
    CalendarService,
    SwapAuditoriumCalendarService,
    CopyWeekAdvancedCalendarService,
    RewardsManagerService,
  ]
};
